/**
 * Gets the latest lesson run that has at least one filled answer.
 *
 * A lesson run counts if any exercise in that run has non-empty answers.
 * Runs where all answers are empty (or missing) are ignored.
 *
 * @param {Array} lessons - Array of lesson objects with id, finished, lessonRunId, and timestamp
 * @param {Array} exercises - Array of exercise objects with lessonRunId and answers
 * @returns {Object|null} - The latest lesson object with filled answers, or null if none found
 */
import hasNonEmptyAnswers from './hasNonEmptyAnswers';

const getLatestLessonWithFilledAnswers = (lessons, exercises) => {
  if (!lessons || !Array.isArray(lessons) || lessons.length === 0) {
    return null;
  }

  const exercisesList = Array.isArray(exercises) ? exercises : [];

  const lessonsWithAnswers = lessons.filter(lesson => {
    if (!lesson || !lesson.finished || !lesson.lessonRunId || !lesson.timestamp) {
      return false;
    }

    const hasFilledAnswers = exercisesList.some(exercise =>
      exercise &&
      exercise.lessonRunId === lesson.lessonRunId &&
      hasNonEmptyAnswers(exercise.answers)
    );

    return hasFilledAnswers;
  });

  if (lessonsWithAnswers.length === 0) {
    return null;
  }

  const nonZeroScoreLessons = lessonsWithAnswers.filter(
    lesson => lesson.score !== null && lesson.score !== undefined && lesson.score !== 0
  );

  const candidates = nonZeroScoreLessons.length > 0
    ? nonZeroScoreLessons
    : lessonsWithAnswers;

  return candidates.reduce((latest, current) => {
    const latestTime = new Date(latest.timestamp).getTime();
    const currentTime = new Date(current.timestamp).getTime();
    return currentTime > latestTime ? current : latest;
  });
};

export default getLatestLessonWithFilledAnswers;
